import {
  ForwardedRef,
  forwardRef,
  KeyboardEventHandler,
  SelectHTMLAttributes,
  useEffect,
  useState,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { FontsModal } from '../../modals/FontsModal'
import { ModalWrapper } from '../../modals/ModalWrapper'
import { TryPremiumModal } from '../../modals/TryPremiumModal'
import { getFonts } from '../../services/fonts'

interface FontsProps extends SelectHTMLAttributes<HTMLSelectElement> {
  variant?: 'primary' | 'secondary' | 'danger'
  subscription: string | null | undefined
}

export const Fonts = forwardRef(
  (
    { variant = 'primary', className, subscription }: FontsProps,
    ref: ForwardedRef<HTMLSelectElement>,
  ) => {
    const variantClasses = {
      primary: 'shadow-md w-full order-blue-500 focus:border-blue-600 mb-5',
      secondary: 'border-gray-300 focus:border-gray-400',
      danger:
        'shadow-md bg-red-50 border border-red-500 text-red-900 ' +
        'placeholder-red-700 text-sm rounded-lg focus:ring-red-500 ' +
        'focus:border-red-500 block w-full p-2.5',
    }

    const { t } = useTranslation()
    const navigate = useNavigate()

    const { watch, setValue } = useFormContext<DesignFormFields>()
    const [showModal, setShowModal] = useState(false)
    const [modalType, setModalType] = useState<'font' | 'premium'>('font')
    const [fonts, setFonts] = useState<Font[] | null>(null)

    // Handle select mouse down
    const handleMouseDown = (
      e: React.MouseEvent<HTMLSelectElement, MouseEvent>,
    ) => {
      e.preventDefault() // Prevent default dropdown behavior
      e.stopPropagation()
      setShowModal(true) // Open the modal with fonts instead
    }

    const getAllFonts = async() => {
      const fonts = (await getFonts()) as Font[]
      setFonts(fonts)
    }

    // Handle keydown events
    const handleKeyDown: KeyboardEventHandler<HTMLSelectElement> = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        setShowModal(true)
      }
    }

    const handleModalClose = () => {
      if (modalType === 'premium') {
        setModalType('font')
      } else {
        setShowModal(false)
      }
    }

    const handleFontChoose = (font_id: number, isPremium: boolean) => {
      if (!subscription && isPremium) {
        setModalType('premium') // Show the modal if user doesn't have subscription
      } else {
        setValue('font_id', font_id) // Set font value
        setShowModal(false)
      }
    }
    // Change keydown handler, depending on modal type
    useEffect(() => {
      getAllFonts()
      document.body.style.overflow = 'hidden'

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          if (modalType === 'font') {
            setShowModal(false)
          } else {
            setModalType('font')
          }
        }
      }

      window.addEventListener('keydown', handleKeyDown)

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }, [modalType])

    return (
      <>
        <p className="mb-6 block text-[18px] font-medium capitalize">
          {t('fonts')}
        </p>
        <div className="relative max-w-[300px]">
          <select
            ref={ref}
            className={`cursor-pointer rounded-md border px-4 py-4 placeholder-black 
          focus:outline-none focus:ring-2 focus:ring-${variant}-500 appearance-none ${variantClasses[variant]} ${className}`}
            onMouseDown={handleMouseDown}
            onKeyDown={handleKeyDown}
            value={watch('font_id')}
            onChange={() => {}}
          >
            {/* First fonts by default */}
            {fonts &&
              fonts.map(({ id, display }) => (
                <option key={id} value={id}>
                  {display}
                </option>
              ))}
          </select>
        </div>

        <ModalWrapper
          showModal={showModal}
          onClose={handleModalClose}
          withHandleKeyDown={false}
        >
          {modalType === 'premium' && (
            <TryPremiumModal
              showModal={showModal}
              onClose={() => {
                setModalType('font')
              }}
              onConfirm={() => {
                setShowModal(false)
                navigate('/pricing')
              }}
            />
          )}

          {fonts && modalType === 'font' && (
            <FontsModal
              fonts={fonts}
              showModal={showModal}
              onChoose={handleFontChoose}
              onClose={() => setShowModal(false)}
              onConfirm={() => {}}
              subscription={subscription}
            />
          )}
        </ModalWrapper>
      </>
    )
  },
)
